// AIPanel.jsx
import React from "react";
import "./AIPanel.scss";

import { PanelControl, IconControl } from "spark-core-dx/components";
import { AISplitPane } from "./AISplitPane"; // adjust path as needed

const AIPanel = ({
  open,
  className,
  presentation = "slideout",
  children,                  // goes to LEFT "Manual"
  title,
  onCancel,
  width,
  onUpdatePanel,
  onSessionChange,
  onOpenAssociatedOutput,
  onAnimationEnd,
  mapToClass,
  panelStorageKey = "ai-panel:width",
  shareLink,
  breakoutLink,
  session,
  aiChats,
  referenceId,
  referenceName,
  params,
  operationName,
  sideBarClassName = "side-navbar",
  minPanelWidth = 360,
  minLeft = 360,
  minRight = 360,
  minMiddle = 360,
  enableContentDragHiding = true,
  chatEnabled = true,
  chatPaneToggleEnabled = true,
  chatPane = "right",        // 'right' | 'middle' | 'left'
  chatPaneCollapseMode = "disabled", // 'disabled' | 'open' | 'closed'
  directModeOnly = false,
  defaultOrchestrationMode = null,
  singleSessionMode = false,
  planningPreviewEnabled = true,
  previewPanelEnabled = false,
  previewRefreshEnabled = null,
  splashMessage = null,
  introMessage = null,
  startChat = null,
  payload = null,
  reviewContext = null,
  contentReady = true,
  loadingContent = null,
  bottomDrawerEnabled = false,
  bottomDrawerDefaultOpen = false,
  bottomDrawerTitle = "Debug",
  bottomDrawerContent = null,
  // optional pane content
  left,
  middle,
  right
}) => {
  const handleShare = (link) => {
    navigator.clipboard.writeText(link);
    // header toasts live inside AISplitPane; if you want a top-level toast here, we can lift state.
  };
  const handleBreakout = (link) => window.open(link, "_blank");
  const handleOnExit = async () => {
    if (onCancel) await onCancel();
  };

  const splitPane = (
    <AISplitPane
      className={className}
      onUpdatePanel={onUpdatePanel}
      onSessionChange={onSessionChange}
      onOpenAssociatedOutput={onOpenAssociatedOutput}
      session = {session}
      aiChats={aiChats}
      referenceId={referenceId}
      referenceName={referenceName}
      params={params}
      operationName={operationName}
      enableContentDragHiding={enableContentDragHiding}
      chatEnabled={chatEnabled}
      chatPaneToggleEnabled={chatPaneToggleEnabled}
      chatPane={chatPane}
      chatPaneCollapseMode={chatPaneCollapseMode}
      directModeOnly={directModeOnly}
      defaultOrchestrationMode={defaultOrchestrationMode}
      singleSessionMode={singleSessionMode}
      planningPreviewEnabled={planningPreviewEnabled}
      previewPanelEnabled={previewPanelEnabled}
      previewRefreshEnabled={previewRefreshEnabled}
      payload={payload}
      reviewContext={reviewContext}
      left={left}
      middle={middle}
      right={right}
      minLeft = {minLeft}
      minMiddle = {minMiddle}
      minRight = {minRight}
      splashMessage = {splashMessage}
      introMessage = {introMessage}
      startChat = {startChat}
      bottomDrawerEnabled={bottomDrawerEnabled}
      bottomDrawerDefaultOpen={bottomDrawerDefaultOpen}
      bottomDrawerTitle={bottomDrawerTitle}
      bottomDrawerContent={bottomDrawerContent}
    >
      {children}
    </AISplitPane>
  );

  const panelContent = contentReady === false
    ? (
      <div className="ai-panel-deferred-content">
        {loadingContent}
      </div>
    )
    : splitPane;

  if (presentation === "inline") {
    if (open === false) return null;

    return (
      <div className="panel__content ai-panel-content-scope">
        {splitPane}
      </div>
    );
  }

  return (
    <PanelControl
      open={open}
      onClose={handleOnExit}
      onAnimationEnd={onAnimationEnd}
      title={title}
      rootClassName="ai-panel"
      className={className ?? ""}
      mapToClass={mapToClass}
      position="right"
      minWidth={minPanelWidth}
      width={width}
      resizable={true}
      storageKey={panelStorageKey}
      sideBarClassName={sideBarClassName}
      contentClassName="ai-panel-content-scope"
      resizeOverlay = {enableContentDragHiding}
      headerActions={
        <>
          {shareLink && <IconControl iconKey={"share"} title={"Share"} onClick={() => handleShare(shareLink)} />}
          {breakoutLink && (
            <IconControl iconKey={"opennewwindow"} title={"Open in new window"} onClick={() => handleBreakout(breakoutLink)} />
          )}
        </>
      }
    >
      {panelContent}
    </PanelControl>
  );
};

export default AIPanel;
